package org.example.project.dashboard

import kotlinx.serialization.builtins.MapSerializer
import kotlinx.serialization.builtins.serializer
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.contentOrNull
import org.example.project.service.UserSettingsApi

internal data class DashboardLayoutPreferences(
    val topbarAutoHide: Boolean = false,
    val sidebarAutoHide: Boolean = false,
    val sidebarCollapsed: Boolean = false,
    val monitorEnabled: Boolean = true,
    val feedPushEnabled: Map<String, Boolean> = emptyMap()
)

private const val DASHBOARD_LAYOUT_TOPBAR_AUTO_HIDE = "topbarAutoHide"
private const val DASHBOARD_LAYOUT_SIDEBAR_AUTO_HIDE = "sidebarAutoHide"
private const val DASHBOARD_LAYOUT_SIDEBAR_COLLAPSED = "sidebarCollapsed"
private const val DASHBOARD_LAYOUT_MONITOR_ENABLED = "dashboardMonitorEnabled"
private const val DASHBOARD_LAYOUT_FEED_VISIBLE_PREFIX = "dashboardFeedVisible."
private const val DASHBOARD_FEEDS_FILE = "dashboardfeeds.json"

private val dashboardFeedsJson = Json {
    prettyPrint = true
    ignoreUnknownKeys = true
}

internal fun dashboardLayoutSettingsKey(art: String): String =
    art.lowercase()

private fun dashboardFeedPushSettingKey(feedKey: String): String =
    "$DASHBOARD_LAYOUT_FEED_VISIBLE_PREFIX$feedKey"

private fun readDashboardFeedPushSettings(settings: Map<String, String>): Map<String, Boolean> =
    DashboardFeedRegistry.definitions.associate { definition ->
        val direct = settings[dashboardFeedPushSettingKey(definition.key)]?.toBooleanStrictOrNull()
        definition.key to (direct ?: true)
    }

private fun decodeDashboardFeeds(raw: String): Map<String, Map<String, String>> {
    if (raw.isBlank()) return emptyMap()
    val body = runCatching {
        val element = dashboardFeedsJson.parseToJsonElement(raw)
        (element as? JsonPrimitive)?.contentOrNull ?: raw
    }.getOrDefault(raw)
    return runCatching {
        dashboardFeedsJson.decodeFromString(
            MapSerializer(String.serializer(), MapSerializer(String.serializer(), String.serializer())),
            body
        )
    }.getOrDefault(emptyMap())
}

internal suspend fun loadDashboardLayoutPreferences(settingsKey: String, forceReload: Boolean): DashboardLayoutPreferences {
    val raw = UserSettingsApi.loadCurrentUserSettingsFile(DASHBOARD_FEEDS_FILE)
    val root = decodeDashboardFeeds(raw)
    val settings = root[settingsKey].orEmpty()
    return DashboardLayoutPreferences(
        topbarAutoHide = settings[DASHBOARD_LAYOUT_TOPBAR_AUTO_HIDE] == "true",
        sidebarAutoHide = settings[DASHBOARD_LAYOUT_SIDEBAR_AUTO_HIDE] == "true",
        sidebarCollapsed = settings[DASHBOARD_LAYOUT_SIDEBAR_COLLAPSED] == "true",
        monitorEnabled = settings[DASHBOARD_LAYOUT_MONITOR_ENABLED] != "false",
        feedPushEnabled = readDashboardFeedPushSettings(settings)
    )
}

internal suspend fun saveDashboardLayoutPreferences(settingsKey: String, preferences: DashboardLayoutPreferences) {
    val raw = UserSettingsApi.loadCurrentUserSettingsFile(DASHBOARD_FEEDS_FILE)
    val current = decodeDashboardFeeds(raw)
    val settings = mapOf(
        DASHBOARD_LAYOUT_TOPBAR_AUTO_HIDE to preferences.topbarAutoHide.toString(),
        DASHBOARD_LAYOUT_SIDEBAR_AUTO_HIDE to preferences.sidebarAutoHide.toString(),
        DASHBOARD_LAYOUT_SIDEBAR_COLLAPSED to preferences.sidebarCollapsed.toString(),
        DASHBOARD_LAYOUT_MONITOR_ENABLED to preferences.monitorEnabled.toString()
    ) + preferences.feedPushEnabled.mapKeys { (feedKey, _) ->
        dashboardFeedPushSettingKey(feedKey)
    }.mapValues { (_, enabled) ->
        enabled.toString()
    }
    val body = dashboardFeedsJson.encodeToString(
        MapSerializer(String.serializer(), MapSerializer(String.serializer(), String.serializer())),
        current + (settingsKey to settings)
    )
    if (UserSettingsApi.saveCurrentUserSettingsFile(DASHBOARD_FEEDS_FILE, body)) {
        org.example.project.service.GlobalManager.markJsonChanged(DASHBOARD_FEEDS_FILE, "user/settings/$DASHBOARD_FEEDS_FILE")
    }
}
